import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "@/assets/vfs_fonts";
import headerPortraitImage from '../../assets/header-landscape.png';
import footerPortraitImage from '../../assets/footer-landscape.png';

pdfMake.vfs = vfs;

const getBase64Image = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = url;
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
});

const formatDate = (value) => {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatSummaryValue = (count, percentage) => {
    const safeCount = count ?? '-';
    const safePercentage = Number(percentage ?? 0);
    return `${safeCount}\n (${safePercentage.toFixed(2)}%)`;
};

export const generateComplianceReportPerOfficePDF = async (reports, officeName, semesterLabel, preparedBy) => {
    if (!reports || !reports.length) return;

    const headerImageBase64 = await getBase64Image(headerPortraitImage);
    const footerImageBase64 = await getBase64Image(footerPortraitImage);

    const body = [
        [
            { text: 'Area of Concern', style: 'tableHeader', alignment: 'center' },
            { text: 'Deadline', style: 'tableHeader', alignment: 'center' },
            { text: 'Before Deadline', style: 'tableHeader', alignment: 'center' },
            { text: 'On Deadline', style: 'tableHeader', alignment: 'center' },
            { text: 'After Deadline', style: 'tableHeader', alignment: 'center' },
            { text: 'Not Submitted', style: 'tableHeader', alignment: 'center' },
            { text: 'Remarks', style: 'tableHeader', alignment: 'center' },
        ],
    ];

    reports.forEach((report, index) => {
        const summary = report?.summary || {};
        const percent = summary.percentage || {};

        body.push([
            { text: report?.compliance?.name || `Compliance ${index + 1}`, margin: [4, 4, 4, 4] },
            { text: formatDate(report?.deadline), alignment: 'center', margin: [4, 4, 4, 4] },
            { text: formatSummaryValue(summary.counts.beforeDeadline, percent.beforeDeadline), alignment: 'center', margin: [4, 4, 4, 4] },
            { text: formatSummaryValue(summary.counts.onDeadline, percent.onDeadline), alignment: 'center', margin: [4, 4, 4, 4] },
            { text: formatSummaryValue(summary.counts.afterDeadline, percent.afterDeadline), alignment: 'center', margin: [4, 4, 4, 4] },
            { text: formatSummaryValue(summary.counts.notSubmitted, percent.notSubmitted), alignment: 'center', margin: [4, 4, 4, 4] },
            {
                text: report?.remarks || '—',
                margin: [4, 8, 4, 8],
                fontSize: 9,
                maxLines: 6,
            },
        ]);
    });

    const docDefinition = {
        defaultStyle: {
            font: 'ArialNarrow',
            fontSize: 10,
        },
        info: {
            title: 'Compliance Report',
        },
        pageSize: {
            width: 612,
            height: 936,
        },
        pageOrientation: 'landscape',
        pageMargins: [35, 115, 35, 115],
        header: (currentPage, pageCount, pageSize) => ({
            stack: [
                {
                    image: headerImageBase64,
                    width: pageSize.width - 40,
                    alignment: 'center',
                    margin: [0, 8, 0, 0],
                },
                {
                    text: 'Office of the Vice President for Academic Affairs',
                    fontSize: 16,
                    bold: true,
                    alignment: 'center',
                    font: 'CenturySchoolBook',
                },
            ],
        }),
        footer: (currentPage, pageCount, pageSize) => ({
            image: footerImageBase64,
            width: pageSize.width - 40,
            alignment: 'center',
            margin: [0, 25, 0, 0],
        }),
        content: [
            {
                text: 'COMPLIANCE REPORT',
                style: 'title',
                alignment: 'center',
                margin: [0, 10, 0, 6],
            },
            {
                text: officeName || 'Office Report',
                style: 'subtitle',
                alignment: 'center',
                margin: [0, 0, 0, 4],
            },
            {
                text: semesterLabel || 'Semester Report',
                style: 'subtitle',
                alignment: 'center',
                margin: [0, 0, 0, 12],
            },
            {
                table: {
                    headerRows: 1,
                    dontBreakRows: true,
                    keepWithHeaderRows: 1,
                    widths: ['*', 70, 70, 70, 70, 70, '*'],
                    body,
                },
                layout: {
                    hLineWidth: (i) => (i === 0 || i === 1 ? 1 : 0.5),
                    vLineWidth: () => 0.5,
                },
                margin: [0, 0, 0, 8],
            },
            {
                stack: [
                    { text: 'Prepared by:', bold: true, margin: [0, 20, 0, 10] },
                    { 
                        text: `${preparedBy.fname} ${
                            preparedBy.mname
                                ? preparedBy.mname[0].toUpperCase() + '. '
                                : ''
                        }${preparedBy.lname}, ${preparedBy.post_nominal || ''}`.trim(),
                        decoration: 'underline',
                        margin: [0, 15, 0, 0]
                    },
                    { text: `${preparedBy.designation}`, bold: false }
                ],
                noWrap: false,
            }
        ],
        styles: {
            title: {
                fontSize: 16,
                bold: true,
            },
            subtitle: {
                fontSize: 13,
                bold: true,
            },
            tableHeader: {
                fontSize: 10,
                bold: true,
                margin: [0, 10, 0, 10],
            },
        },
    };

    pdfMake.createPdf(docDefinition).open();
};
